// ============================================================================
// Resumen impreso desde la caja de contingencia (paso 3c · oct-2026).
//
// Lo mismo que el resumen del POS: un papel para que la mesa revise antes de
// pagar, que dice claramente que NO es factura. Se arma en el navegador, así
// que funciona sin internet con la impresora del PC de caja.
// ============================================================================
import { formatMoneyRedondo } from '@/lib/moneda';
import * as caja from '../../../lib/caja-contingencia';
import { nombreDestino, type PedidoContingencia } from '../../../lib/caja-contingencia';
import type { TraductorCaja } from './caja-contingencia';

const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** Abre la ventana de impresión. Devuelve false si el navegador bloqueó la ventana. */
export function imprimirResumenContingencia(p: PedidoContingencia, sede: string, t: TraductorCaja): boolean {
  const fmt = (n: number) => formatMoneyRedondo(Number(n) || 0);
  const filas = [
    ...(p.servidor && !p.servidor.facturado ? p.servidor.lineas.map(l => ({ cantidad: l.cantidad, nombre: l.nombre, total: l.subtotal })) : []),
    ...p.lineas.map(l => ({ cantidad: l.cantidad, nombre: l.nombre, total: l.precio * l.cantidad })),
  ].map(f => `<tr><td class="n">${f.cantidad}</td><td>${esc(f.nombre)}</td><td class="n">${fmt(f.total)}</td></tr>`).join('');
  const titulo = `${t('resumenTitulo')}: ${esc(nombreDestino(p))}${p.servidor ? ` (${esc(p.servidor.referencia)})` : ''}`;
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${titulo}</title>
    <style>
    *{box-sizing:border-box;} body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:22px;color:#0E1626;max-width:430px;margin:0 auto;}
    h1{font-size:17px;margin:0 0 2px;} .sub{font-size:12px;color:#667;margin:0 0 12px;}
    .aviso{font-size:11.5px;font-weight:700;color:#8a5a00;background:#FFF6E0;border:1px solid #F0D79A;border-radius:7px;padding:7px 9px;margin-bottom:12px;}
    table{width:100%;border-collapse:collapse;font-size:12.5px;} th,td{text-align:left;padding:7px 8px;border-bottom:1px solid #E6E9EF;}
    th{font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;color:#667;} td.n,th.n{text-align:right;white-space:nowrap;}
    .tot{display:flex;justify-content:space-between;font-size:15px;font-weight:800;margin-top:12px;}
    @media print{.noprint{display:none;}}</style></head><body>
    <h1>${titulo}</h1>
    <p class="sub">${esc(sede)} · ${new Date().toLocaleString('es-CO')}</p>
    <div class="aviso">${esc(t('resumenAviso'))}<br>${esc(t('resumenContingencia'))}</div>
    <table><thead><tr><th class="n">${esc(t('cant'))}</th><th>${esc(t('item'))}</th><th class="n">${esc(t('total'))}</th></tr></thead>
    <tbody>${filas}</tbody></table>
    <div class="tot"><span>${esc(t('consumo'))}</span><span>${fmt(caja.consumo(p))}</span></div>
    <p class="sub" style="margin-top:12px;">${esc(t('resumenNota'))}</p>
    <button class="noprint" onclick="window.print()" style="margin-top:12px;padding:8px 16px;border:0;border-radius:8px;background:#1f4fd8;color:#fff;font-weight:600;cursor:pointer;">${esc(t('imprimir'))}</button>
    </body></html>`;
  const w = window.open('', '_blank', 'width=470,height=700');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch { /* el cajero imprime con el botón */ } }, 350);
  return true;
}
